/**
 * Data layer for `automatic-let-s-encrypt-renewal-not-manual-ticket`.
 * Typed fetch wrappers over /api/v1/automatic-let-s-encrypt-renewal-not-manual-ticket.
 * Raw `fetch()` is confined to this file per the dash contract (D6).
 */

export type CertStatus =
	| "pending"
	| "active"
	| "renewing"
	| "failed"
	| "expired";

export interface LetsEncryptCertRow {
	id: number;
	tenant_id: string;
	domain: string;
	cert_status: CertStatus;
	cert_authority: string;
	issued_at: number | null;
	expires_at: number | null;
	next_renewal_at: number | null;
	last_renewal_attempt_at: number | null;
	last_error: string | null;
	auto_renew: number;
	created_at: number;
	updated_at: number;
}

export interface ListCertsResponse {
	items: LetsEncryptCertRow[];
}

export interface RegisterDomainBody {
	domain: string;
	auto_renew?: boolean;
}

export interface RegisterDomainResponse {
	ok: true;
	item: LetsEncryptCertRow | null;
}

export interface RenewCertResponse {
	ok: true;
	item: LetsEncryptCertRow;
}

const BASE = "/api/v1/automatic-let-s-encrypt-renewal-not-manual-ticket";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function listCerts(): Promise<ListCertsResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListCertsResponse>(res);
}

export async function registerDomain(
	body: RegisterDomainBody,
): Promise<RegisterDomainResponse> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<RegisterDomainResponse>(res);
}

export async function forceRenewCert(id: number): Promise<RenewCertResponse> {
	const res = await fetch(`${BASE}/${encodeURIComponent(String(id))}/renew`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify({}),
	});
	return parseOrThrow<RenewCertResponse>(res);
}

/**
 * Returns integer days until cert expires. Null/unknown → null.
 * Negative values indicate the cert has already expired.
 */
export function daysUntilExpire(
	expires_at: number | null,
	now_secs: number = Math.floor(Date.now() / 1000),
): number | null {
	if (expires_at === null || expires_at === undefined) return null;
	if (!Number.isFinite(expires_at)) return null;
	const diff_secs = expires_at - now_secs;
	// Use trunc so -0.5 days stays -0 not -1.
	return Math.trunc(diff_secs / 86400);
}

export function formatExpirySecs(secs: number | null): string {
	if (secs === null || secs === undefined) return "—";
	if (!Number.isFinite(secs)) return "—";
	const d = new Date(secs * 1000);
	if (Number.isNaN(d.getTime())) return "—";
	return d.toISOString().slice(0, 10);
}

export const letsEncryptQueryKeys = {
	all: ["automatic-let-s-encrypt-renewal-not-manual-ticket"] as const,
	list: () =>
		[...letsEncryptQueryKeys.all] as const,
	detail: (id: number) =>
		[...letsEncryptQueryKeys.all, id] as const,
};
